"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.decodePageDocument = decodePageDocument;
const decoded_types_1 = require("../decoded-types");
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const entity_list_1 = require("./entity-list");
const typeCodes_1 = require("../typeCodes");
const decode_prop_block_1 = require("./decode-prop-block");
const decode_tc5_1 = require("./decode-tc5");
const decode_tc6_tc14_tc55_1 = require("./decode-tc6-tc14-tc55");
const decode_graphics_annotation_1 = require("./decode-graphics-annotation");
const decode_tc7_tc12_1 = require("./decode-tc7-tc12");
var SLOT;
(function (SLOT) {
    SLOT[SLOT["pageInstance"] = 0] = "pageInstance";
    SLOT[SLOT["headContainer1"] = 1] = "headContainer1";
    SLOT[SLOT["headContainer2"] = 2] = "headContainer2";
    SLOT[SLOT["headSeparator"] = 3] = "headSeparator";
    SLOT[SLOT["metadataStart"] = 4] = "metadataStart";
    SLOT[SLOT["metadataEnd"] = 9] = "metadataEnd";
    SLOT[SLOT["pageMetadata"] = 10] = "pageMetadata";
    SLOT[SLOT["netStubs"] = 11] = "netStubs";
    SLOT[SLOT["pageBorder"] = 12] = "pageBorder";
    SLOT[SLOT["powerSymbols"] = 13] = "powerSymbols";
    SLOT[SLOT["componentInstances"] = 14] = "componentInstances";
    SLOT[SLOT["graphics"] = 15] = "graphics";
    SLOT[SLOT["tailFlag"] = 16] = "tailFlag";
    SLOT[SLOT["tailMarker"] = 17] = "tailMarker";
    SLOT[SLOT["tailHashMarker"] = 18] = "tailHashMarker";
    SLOT[SLOT["pageProperties"] = 19] = "pageProperties";
})(SLOT || (SLOT = {}));
const GRAPHICS_TYPE_CODES = new Set([
    typeCodes_1.TypeCode.GraphicsLineArrow,
    typeCodes_1.TypeCode.GraphicsCallout,
    typeCodes_1.TypeCode.GraphicsHtml,
    typeCodes_1.TypeCode.GraphicsImage,
]);
function decodePageDocument(root, path, warnings, instanceHashMap, pageNum) {
    const c = root.children;
    if (c.length !== 20) {
        warnings.push({
            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
            message: `page-root has ${c.length} children, expected 20`,
            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            path,
        });
    }
    const pageInstance = decodeSingletonEntityList(c[SLOT.pageInstance], typeCodes_1.TypeCode.PageInstance, `${path}/pageInstance`, warnings, (item, p) => (0, decode_tc6_tc14_tc55_1.decodePageInstance)(item, p, warnings));
    const pageMetadata = decodeSingletonEntityList(c[SLOT.pageMetadata], typeCodes_1.TypeCode.PageMetadata, `${path}/pageMetadata`, warnings, (item, p) => (0, decode_tc6_tc14_tc55_1.decodePageMetadata)(item, p, warnings));
    const pageBorder = decodeSingletonEntityList(c[SLOT.pageBorder], typeCodes_1.TypeCode.PageBorder, `${path}/pageBorder`, warnings, (item, p) => (0, decode_tc6_tc14_tc55_1.decodePageBorder)(item, p, warnings));
    const netStubs = decodeOrRaw(c[SLOT.netStubs], `${path}/netStubs`, warnings, (n, p, w) => (0, entity_list_1.decodeEntityList)(n, typeCodes_1.TypeCode.NetStub, p, w, (item, _i, itemPath) => (0, decode_tc5_1.decodeNetStub)(item, itemPath, w)));
    const powerSymbols = decodeConditionalSlot(c[SLOT.powerSymbols], `${path}/powerSymbols`, warnings, (n, p, w) => (0, entity_list_1.decodeEntityList)(n, typeCodes_1.TypeCode.PowerSymbol, p, w, (item, _i, itemPath) => (0, decode_tc7_tc12_1.decodePowerSymbol)(item, itemPath, w)));
    const componentInstances = decodeConditionalSlot(c[SLOT.componentInstances], `${path}/componentInstances`, warnings, (n, p, w) => (0, entity_list_1.decodeEntityList)(n, typeCodes_1.TypeCode.ComponentInstance, p, w, (item, _i, itemPath) => (0, decode_tc7_tc12_1.decodeComponentInstance)(item, itemPath, w, instanceHashMap, pageNum)));
    const graphics = decodeGraphicsSlot(c[SLOT.graphics], `${path}/graphics`, warnings);
    const pagePropertiesNode = c[SLOT.pageProperties];
    const pageProperties = pagePropertiesNode === undefined ? null : (0, decode_prop_block_1.decodePropBlock)(pagePropertiesNode, `${path}/pageProperties`, warnings);
    const metadataAtoms = c.slice(SLOT.metadataStart, SLOT.metadataEnd + 1);
    const pageNameAtom = metadataAtoms.find((n) => n.type === 'atom' && /[A-Za-z]/.test(n.content));
    const pageName = pageNameAtom === undefined || pageNameAtom.type !== 'atom' ? null : pageNameAtom.content.trim();
    const slots = {
        pageInstance: pageInstance ?? c[SLOT.pageInstance],
        headContainer1: c[SLOT.headContainer1],
        headContainer2: c[SLOT.headContainer2],
        headSeparator: c[SLOT.headSeparator],
        metadataAtoms,
        pageName,
        pageMetadata: pageMetadata ?? c[SLOT.pageMetadata],
        netStubs: netStubs ?? c[SLOT.netStubs],
        pageBorder: pageBorder ?? c[SLOT.pageBorder],
        powerSymbols,
        componentInstances,
        graphics,
        tailFlag: c[SLOT.tailFlag],
        tailMarker: c[SLOT.tailMarker],
        tailHashMarker: c[SLOT.tailHashMarker],
        pageProperties: pageProperties ?? c[SLOT.pageProperties],
    };
    return {
        kind: 'page',
        slots,
        raw: root,
    };
}
function decodeSingletonEntityList(node, typeCode, path, warnings, decodeItem) {
    return (0, entity_list_1.decodeEntityList)(node, typeCode, path, warnings, (item, _i, itemPath) => decodeItem(item, itemPath));
}
function decodeConditionalSlot(node, path, warnings, decoder) {
    if (node === undefined) {
        return null;
    }
    if ((0, entity_list_1.isPlaceholderContainer)(node)) {
        return null;
    }
    const decoded = decoder(node, path, warnings);
    return decoded ?? node;
}
function decodeGraphicsSlot(node, path, warnings) {
    if (node === undefined) {
        return null;
    }
    if ((0, entity_list_1.isPlaceholderContainer)(node)) {
        return null;
    }
    if (node.type !== 'container' && node.type !== 'econtainer') {
        warnings.push({
            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
            message: `graphics slot is not a container (${node.type})`,
            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            path,
        });
        return node;
    }
    const detected = (0, entity_list_1.detectEntityList)(node.children);
    if (detected === null) {
        warnings.push({
            type: decoded_types_1.DecodeWarningType.UnrecognizedShape,
            message: `graphics slot does not look like an entity-list`,
            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            path,
        });
        return node;
    }
    const typeCode = (0, typeCodes_1.asTypeCode)(detected.typeCode);
    if (typeCode === null || !GRAPHICS_TYPE_CODES.has(typeCode)) {
        warnings.push({
            type: decoded_types_1.DecodeWarningType.UnknownTypeCode,
            message: `graphics slot holds unfamiliar typeCode=${detected.typeCode}`,
            severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            path,
        });
        return node;
    }
    return (0, entity_list_1.decodeEntityList)(node, typeCode, path, warnings, (item, _i, itemPath) => (0, decode_graphics_annotation_1.decodeGraphicsAnnotation)(item, typeCode, itemPath, warnings));
}
function decodeOrRaw(node, path, warnings, decoder) {
    if (node === undefined) {
        return null;
    }
    const decoded = decoder(node, path, warnings);
    return decoded ?? node;
}
//# sourceMappingURL=decode-page-document.js.map